import { Box } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormChip } from "@/forms-design-system/FormChip/FormChip";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Copy } from "@/icons/outlined";

interface CopyRowProps {
  label: string;
  value: string;
  onCopy?: () => void;
}

export const CopyRow: FC<CopyRowProps> = ({ label, value, onCopy }) => {
  const { t } = useTranslate();
  const suffixStart = Math.max(value.lastIndexOf(" ") + 1, value.length - 8);
  const [copied, setCopied] = useState(false);
  const timeout = useRef<number | undefined>(undefined);
  // Clear the clipboard feedback timer when the donor leaves this screen.
  useEffect(() => () => window.clearTimeout(timeout.current), []);

  return (
    <FormTypography
      variant="body2"
      vibe="quiet"
      sx={{ overflowWrap: "anywhere", "&:hover [data-copy], &:focus-within [data-copy]": { opacity: 1 } }}
    >
      {label}: {value.slice(0, suffixStart)}
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {value.slice(suffixStart)}
        {copied ? (
          <FormChip
            role="status"
            variant="outlined"
            vibe="neutral"
            label={t("common", "daf.copied")}
            sx={{ display: "inline-flex", verticalAlign: "middle", ml: 0.5 }}
          />
        ) : (
          <FormButton
            variant="ghost"
            size="small"
            iconButton
            startIcon={<Copy />}
            aria-label={`${label}: ${value}`}
            data-copy
            data-test="daf-copy"
            sx={(theme) => ({
              opacity: 0,
              color: "inherit",
              "&:hover": { backgroundColor: "transparent" },
              padding: 0,
              minWidth: 0,
              minHeight: 0,
              display: "inline-flex",
              verticalAlign: "middle",
              ml: 0.5,
              width: theme.spacing(2),
              height: theme.spacing(2),
              "@media (hover: none)": { opacity: 1, width: theme.spacing(3), height: theme.spacing(3) },
            })}
            onClick={async () => {
              const didCopy = await navigator.clipboard.writeText(value).then(
                () => true,
                () => false
              );
              if (didCopy) {
                onCopy?.();
                window.clearTimeout(timeout.current);
                setCopied(true);
                timeout.current = window.setTimeout(() => setCopied(false), 3000);
              }
            }}
          />
        )}
      </Box>
    </FormTypography>
  );
};
